import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TaskRo, useWorkspacesControllerGetRootFolderQuery } from 'src/app/services/api.generated';
import { api } from 'src/app/services/api';
import { useAppDispatch } from 'src/app/reduxCustomHooks';
import { useEditPageTask } from 'src/components/hooks/useEditPageTask';
import { taskGetComparator } from 'src/utils/sortTasksUtils';
import { shallowEqual } from 'react-redux';

export type EditNavLocationState = {
  folderId?: string;
  workspaceId?: string;
};

const sortSiblingTasks = (tasks: TaskRo[]) =>
  [...tasks]
    .filter((task) => !task.isDeleted)
    .sort(taskGetComparator('desc', 'createdAt'));

/**
 * Prev/next task in the same folder (else workspace root), matching TasksTable default sort.
 */
export const useSiblingEditNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const locationState = (location.state || {}) as EditNavLocationState;

  const { taskId, workspaceId } = useEditPageTask(
    (task) => ({
      taskId: task?.id,
      workspaceId: task?.workspace?.id,
    }),
    shallowEqual
  );

  const effectiveWorkspaceId = workspaceId || locationState.workspaceId;

  const { data: rootFolder } = useWorkspacesControllerGetRootFolderQuery(
    { id: effectiveWorkspaceId as string },
    { skip: !effectiveWorkspaceId }
  );

  const [siblingTasks, setSiblingTasks] = useState<TaskRo[]>([]);

  useEffect(() => {
    let cancelled = false;

    const fetchFolder = async (folderId: string) => {
      const result = await dispatch(
        api.endpoints.workspacesControllerGetFolderById.initiate({
          workspaceId: effectiveWorkspaceId as string,
          folderId,
        })
      );
      if ('data' in result && result.data) {
        return result.data;
      }
      return null;
    };

    const tryFolder = async (folderId: string): Promise<TaskRo[] | null> => {
      const folder = await fetchFolder(folderId);
      if (!folder) return null;
      if (folder.tasks.some((t) => t.id === taskId)) {
        return folder.tasks;
      }
      for (const sub of folder.folders || []) {
        const found = await tryFolder(sub.id);
        if (found) return found;
      }
      return null;
    };

    const resolveSiblings = async () => {
      if (!effectiveWorkspaceId || !taskId || !rootFolder) {
        if (!cancelled) setSiblingTasks([]);
        return;
      }

      // Prefer explicit folder from navigation state.
      if (locationState.folderId) {
        const fromState = await tryFolder(locationState.folderId);
        if (!cancelled && fromState) {
          setSiblingTasks(sortSiblingTasks(fromState));
          return;
        }
      }

      // Root folder first (workspace-level list).
      if (rootFolder.tasks.some((t) => t.id === taskId)) {
        if (!cancelled) {
          setSiblingTasks(sortSiblingTasks(rootFolder.tasks));
        }
        return;
      }

      // Search nested folders under root.
      for (const sub of rootFolder.folders || []) {
        const found = await tryFolder(sub.id);
        if (found) {
          if (!cancelled) setSiblingTasks(sortSiblingTasks(found));
          return;
        }
      }

      // Fallback: workspace root task list.
      if (!cancelled) {
        setSiblingTasks(sortSiblingTasks(rootFolder.tasks));
      }
    };

    void resolveSiblings();
    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    effectiveWorkspaceId,
    locationState.folderId,
    rootFolder,
    taskId,
  ]);

  const { prevTask, nextTask } = useMemo(() => {
    if (!taskId || siblingTasks.length === 0) {
      return { prevTask: undefined, nextTask: undefined };
    }
    const index = siblingTasks.findIndex((t) => t.id === taskId);
    if (index < 0) {
      return { prevTask: undefined, nextTask: undefined };
    }
    return {
      prevTask: index > 0 ? siblingTasks[index - 1] : undefined,
      nextTask:
        index < siblingTasks.length - 1 ? siblingTasks[index + 1] : undefined,
    };
  }, [siblingTasks, taskId]);

  const navState = useMemo<EditNavLocationState>(
    () => ({
      folderId: locationState.folderId,
      workspaceId: effectiveWorkspaceId,
    }),
    [effectiveWorkspaceId, locationState.folderId]
  );

  const goToTask = useCallback(
    (id: string | undefined) => {
      if (!id) return;
      // Edits auto-save via API; no dirty gate needed.
      navigate(`/edit/${id}`, { state: navState });
    },
    [navState, navigate]
  );

  const goPrevTask = useCallback(() => {
    goToTask(prevTask?.id);
  }, [goToTask, prevTask?.id]);

  const goNextTask = useCallback(() => {
    goToTask(nextTask?.id);
  }, [goToTask, nextTask?.id]);

  return {
    prevTask,
    nextTask,
    goPrevTask,
    goNextTask,
    canGoPrev: !!prevTask,
    canGoNext: !!nextTask,
  };
};
